import { QUESTION_TYPES } from '@api/instant_survey';

import { createQuestionKey } from '../../../../../utilities/helpers';
import { QUESTION_GROUP_DROPPABLE_PREFIX } from '../components/QuestionGroupBlock';

/**
 * buildRenderList
 * Derives a two-level render list from the flat question order and parent-linked question groups.
 * Ungrouped questions and SequentialMonadic questions are passed through as root-level items.
 *
 * Returns an array of items shaped:
 *   { type: 'question', question, flatIndex } |
 *   { type: 'group', groupId, items, questions, flatIndexStart }
 *
 * flatIndex(Start) is the index of the item (or first item of the group) in the flat questions array
 * — used for drag-and-drop positional math.
 *
 * When `enableGroups` is false (QUESTION_GROUP_BLOCKS gate off), grouping is skipped entirely and
 * every question is rendered flat, even if it already carries questionGroup data.
 */
export const buildRenderList = (questions, questionGroups = {}, { enableGroups = true } = {}) => {
  const isGroupable = (question) =>
    enableGroups &&
    question.questionGroup != null &&
    question.type !== QUESTION_TYPES.SEQUENTIAL_MONADIC.value;

  const items = [];
  const findOrCreateGroupItem = (groupItems, groupId, flatIndex) => {
    let groupItem = groupItems.find((item) => item.type === 'group' && item.groupId === groupId);

    if (!groupItem) {
      groupItem = {
        flatIndexStart: flatIndex,
        groupId,
        items: [],
        questions: [],
        type: 'group',
      };
      groupItems.push(groupItem);
    }

    groupItem.flatIndexStart = Math.min(groupItem.flatIndexStart, flatIndex);
    return groupItem;
  };

  questions.forEach((question, flatIndex) => {
    const questionItem = { flatIndex, question, type: 'question' };
    if (!isGroupable(question)) {
      items.push(questionItem);
      return;
    }

    const groupId = question.questionGroup;
    const parentGroupId = questionGroups[String(groupId)]?.parentGroup ?? null;
    const rootGroupItem = findOrCreateGroupItem(items, parentGroupId ?? groupId, flatIndex);
    rootGroupItem.questions.push(question);

    if (parentGroupId == null) {
      rootGroupItem.items.push(questionItem);
      return;
    }

    const childGroupItem = findOrCreateGroupItem(rootGroupItem.items, groupId, flatIndex);
    childGroupItem.questions.push(question);
    childGroupItem.items.push(questionItem);
  });

  return items;
};

export const findGroupRenderItem = (items, groupId) => {
  const rootGroupItem = items.find((item) => item.type === 'group' && item.groupId === groupId);
  if (rootGroupItem) return rootGroupItem;

  return (
    items
      .filter((item) => item.type === 'group')
      .flatMap((item) => item.items)
      .find((item) => item.type === 'group' && item.groupId === groupId) ?? null
  );
};

export const getAvailableParentGroupsByGroupId = (questionGroups = {}) => {
  const groups = Object.values(questionGroups);
  const parentGroupIds = new Set(
    groups.map(({ parentGroup }) => parentGroup).filter((parentGroup) => parentGroup != null)
  );

  return Object.fromEntries(
    groups.map((group) => {
      const availableParentGroups = parentGroupIds.has(group.id)
        ? []
        : groups.filter(
            (candidate) =>
              candidate.parentGroup == null &&
              candidate.id !== group.id &&
              candidate.id !== group.parentGroup
          );
      return [String(group.id), availableParentGroups];
    })
  );
};

export const getRenderItemQuestions = (item) =>
  item.type === 'group' ? item.questions : [item.question];

export const reorderQuestionsByRenderItems = ({
  destinationIndex,
  items,
  questions,
  sourceIndex,
}) => {
  const sourceItem = items[sourceIndex];
  const destinationItem = items[destinationIndex];
  if (!sourceItem || !destinationItem) return questions;

  const movingKeys = new Set(getRenderItemQuestions(sourceItem).map(createQuestionKey));
  const isMoving = (question) => movingKeys.has(createQuestionKey(question));
  const movingQuestions = questions.filter(isMoving);
  const stationaryQuestions = questions.filter((question) => !isMoving(question));
  const destinationFlatIndex =
    destinationItem.type === 'group' ? destinationItem.flatIndexStart : destinationItem.flatIndex;
  const removedBeforeDestination = questions.slice(0, destinationFlatIndex).filter(isMoving).length;
  let insertAt = destinationFlatIndex - removedBeforeDestination;

  if (destinationIndex > sourceIndex) {
    insertAt += getRenderItemQuestions(destinationItem).length;
  }

  const updatedQuestions = [...stationaryQuestions];
  updatedQuestions.splice(
    Math.max(0, Math.min(insertAt, stationaryQuestions.length)),
    0,
    ...movingQuestions
  );
  return updatedQuestions;
};

/**
 * getGroupIdFromDroppableId
 * Returns the numeric group id embedded in a question-group droppable id, or null.
 */
export const getGroupIdFromDroppableId = (droppableId = '') => {
  const prefix = `${QUESTION_GROUP_DROPPABLE_PREFIX}-`;
  if (!droppableId.startsWith(prefix)) return null;
  const id = Number(droppableId.slice(prefix.length));
  return isNaN(id) ? null : id;
};
